import { useRef, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogContent,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import {
	getCurrentSystemMessage,
	getSystemMessageQueue,
	getSystemMessageIndex,
	getSystemMessageError,
	setErrorKey,
	ISystemMessage,
} from 'state/reducers/system-messages.reducer';
import { confirmSystemMessage } from 'services/system-messages.services';
import { getMobile } from 'state/reducers/ui.reducers';
import { selectTranslations, getDir } from 'state/reducers/translation.reducers';
import WarningDialog from 'components/WarningDialog';
import { ReactComponent as RtlConfirmArrowIcon } from 'assets/icons/rtl-confirm-arrow.svg';
import {
	formatSystemMessageCounter,
	resolveSystemMessageIcon,
} from 'constants/system-messages.constants';

type Direction = 'rtl' | 'ltr';
type Translate = (key: string, fallback?: string) => string;

type SystemMessageDialogProps = {
	message: ISystemMessage;
	queueSize: number;
	currentIndex: number;
	isMobile: boolean;
	isConfirming: boolean;
	dir: Direction;
	t: Translate;
	onConfirm: () => void;
};

export const SystemMessageModal = () => {
	const t = useAppSelector(selectTranslations);
	const dir = useAppSelector(getDir) as Direction;
	const dispatch = useAppDispatch();
	const current = useAppSelector(getCurrentSystemMessage);
	const queue = useAppSelector(getSystemMessageQueue);
	const index = useAppSelector(getSystemMessageIndex);
	const isMobile = useAppSelector(getMobile);
	const errorKey = useAppSelector(getSystemMessageError);
	const [isConfirming, setIsConfirming] = useState(false);
	const confirmingRef = useRef(false);
	const total = queue.length;

	const handleConfirm = async () => {
		if (!current || isConfirming || confirmingRef.current) {
			return;
		}

		confirmingRef.current = true;
		setIsConfirming(true);
		try {
			await dispatch(confirmSystemMessage(current.id));
		} finally {
			confirmingRef.current = false;
			setIsConfirming(false);
		}
	};

	return (
		<>
			{current && !errorKey && (
				<SystemMessageDialog
					message={current}
					queueSize={total}
					currentIndex={index}
					isMobile={isMobile}
					isConfirming={isConfirming}
					dir={dir}
					t={t}
					onConfirm={handleConfirm}
				/>
			)}
			{errorKey && (
				<WarningDialog
					open={true}
					subTitle={errorKey}
					handleCancel={() => dispatch(setErrorKey(null))}
				/>
			)}
		</>
	);
};

const SystemMessageDialog = ({
	message,
	queueSize,
	currentIndex,
	isMobile,
	isConfirming,
	dir,
	t,
	onConfirm,
}: SystemMessageDialogProps) => {
	const theme = useTheme();
	const { Icon: MessageIcon, labelKey: iconLabelKey } = resolveSystemMessageIcon(message.iconType);
	const showQueueUi = queueSize > 1;

	return (
		<Dialog
			open={true}
			dir={dir}
			disableEscapeKeyDown
			maxWidth={isMobile ? 'xs' : 'sm'}
			fullWidth
			aria-labelledby="system-message-title"
			aria-describedby="system-message-content"
			sx={{ direction: dir }}
			PaperProps={{
				sx: {
					borderRadius: '16px',
					textAlign: 'start',
					width: isMobile ? 'calc(100vw - 32px)' : 520,
					maxWidth: isMobile ? 'calc(100vw - 32px)' : 520,
					maxHeight: isMobile ? '80vh' : 'calc(100vh - 100px)',
					boxShadow: '0 16px 48px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08)',
				},
			}}
		>
			<DialogContent
				dir={dir}
				sx={{
					padding: isMobile ? '24px 20px 8px' : '32px 32px 0',
					textAlign: 'start',
				}}
			>
				<Stack
					dir={dir}
					sx={{
						width: '100%',
						textAlign: 'start',
						alignItems: 'flex-start',
					}}
				>
					{showQueueUi && (
						<Box
							sx={{
								position: 'absolute',
								top: isMobile ? 14 : 24,
								insetInlineEnd: isMobile ? 16 : 28,
								fontSize: isMobile ? 11 : 12,
								color: '#95afb1',
								background: '#fafcfc',
								border: '1px solid #c7e5de',
								padding: isMobile ? '2px 10px' : '3px 10px',
								borderRadius: '20px',
							}}
						>
							{formatSystemMessageCounter(t('portal.systemMessage.counter'), currentIndex, queueSize)}
						</Box>
					)}
					<Box
						sx={{
							width: 48,
							height: 48,
							borderRadius: '50%',
							background: 'rgba(0,71,81,0.07)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							marginBottom: isMobile ? '16px' : '20px',
							alignSelf: 'flex-start',
						}}
						aria-label={t(iconLabelKey)}
					>
						<MessageIcon width={22} height={22} />
					</Box>
					<Typography
						id="system-message-title"
						variant="h6"
						sx={(muiTheme) => ({
							color: muiTheme.palette.root.formTitle,
							textAlign: 'start',
							width: '100%',
							alignSelf: 'stretch',
							fontSize: isMobile ? 18 : 20,
							fontWeight: 700,
							lineHeight: 1.4,
							marginBottom: isMobile ? '12px' : '16px',
						})}
					>
						{t(message.titleKey)}
					</Typography>
					<Box
						component="hr"
						aria-hidden
						sx={{
							width: '100%',
							alignSelf: 'stretch',
							flexShrink: 0,
							border: 0,
							borderTop: '1px solid #e6efef',
							height: 0,
							marginBottom: isMobile ? '14px' : '18px',
						}}
					/>
					<Box
						component="div"
						id="system-message-content"
						dir="auto"
						sx={(muiTheme) => ({
							width: '100%',
							alignSelf: 'stretch',
							overflowWrap: 'anywhere',
							wordBreak: 'break-word',
							fontSize: isMobile ? 14 : 14.5,
							lineHeight: isMobile ? 1.7 : 1.75,
							color: '#2c3e50',
							fontWeight: 400,
							marginBottom: isMobile ? '8px' : '28px',
							textAlign: 'start',
							'& p': {
								display: 'block',
								width: '100%',
								marginBottom: '10px',
							},
							'& p:last-child': { marginBottom: 0 },
							'& a': {
								color: muiTheme.palette.root.extraGreen,
								textDecoration: 'underline',
								fontWeight: 500,
							},
						})}
						dangerouslySetInnerHTML={{ __html: t(message.contentKey) }}
					/>
				</Stack>
			</DialogContent>
			<Stack
				direction="row"
				alignItems="center"
				dir={dir}
				sx={{
					padding: isMobile ? '14px 20px 16px' : '0 32px 32px',
					flexDirection: isMobile ? 'column' : 'row',
					justifyContent: 'flex-end',
					alignItems: isMobile ? 'stretch' : 'center',
					gap: isMobile ? '12px' : '16px',
				}}
			>
				{showQueueUi && (
					<Box
						sx={{
							marginInlineEnd: isMobile ? 0 : 'auto',
							...(isMobile && { display: 'flex', justifyContent: 'center' }),
						}}
					>
						<Stack direction="row" gap="6px" justifyContent="center" aria-hidden>
							{Array.from({ length: queueSize }).map((_, dotIndex) => {
								const isActive = dotIndex === currentIndex;

								return (
									<Box
										key={dotIndex}
										sx={{
											width: isActive ? 24 : 8,
											height: 8,
											borderRadius: isActive ? '4px' : '50%',
											background: isActive ? theme.palette.root.basicDeepGreen : '#c7e5de',
										}}
									/>
								);
							})}
						</Stack>
					</Box>
				)}
				<Button
					variant="contained"
					onClick={onConfirm}
					disabled={isConfirming}
					aria-busy={isConfirming}
					sx={(muiTheme) => ({
						minWidth: isMobile ? '100%' : 140,
						background: muiTheme.palette.root.basicDeepGreen,
						borderRadius: isMobile ? '12px' : '10px',
						padding: isMobile ? '14px 24px' : '12px 36px',
						fontSize: isMobile ? 16 : 15,
						fontWeight: 600,
						boxShadow: 'none',
						'&:hover': {
							background: '#00262a',
							boxShadow: 'none',
						},
					})}
					endIcon={dir === 'rtl' ? <RtlConfirmArrowIcon /> : undefined}
				>
					{t('portal.systemMessage.confirm_button.text')}
				</Button>
			</Stack>
		</Dialog>
	);
};
